import { useEffect, useState } from "react"
import { useMatches, useNavigate } from "react-router-dom"
import { Box, Button, Divider, ListItemIcon, Menu, MenuItem, Typography } from "@mui/material"
import {
    faAnglesUpDown,
    faChartSimple,
    faSliders,
    faFileInvoiceDollar,
    faQuestionCircle,
} from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { makeStyles } from "tss-react/mui"
import { getAccountingRoute, getAdminRoute, getReportRoute } from "../../../constants/routes"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { useAppSelector } from "../../../modules/store/storeHooks"
import { selectEffectiveUserType } from "../../../modules/context/contextSelectors"
import { UserType } from "../../../modules/login/loginTypes"
import { isObjectWithProp } from "../../../lib/lang"
import { IconDefinition } from "@fortawesome/pro-light-svg-icons"
import { MatchContextUserType } from "../../../modules/accessControl/MatchContextUserType"

enum EnvDropdownValue {
    REPORTS = "REPORTS",
    ADMIN = "ADMIN",
    ACCOUNTING = "ACCOUNTING",
}

const useStyles = makeStyles()((theme) => ({
    envSelectButton: {
        color: theme.palette.tailwind.slate[100],
        marginLeft: 0,
        display: "flex",
        justifyContent: "space-between",
        padding: ".5rem 1rem",
        margin: "0rem 1rem",
        borderRadius: 0,
        textTransform: "none",
    },
    menu: {
        marginTop: "1rem",
    },
    menuItem: {
        maxWidth: "20rem",
        minWidth: "17rem",
        display: "flex",
        alignItems: "start",
    },
    menuItemSubtitle: {
        maxWidth: "20rem",
        fontSize: ".75rem",
        textWrap: "wrap",
    },
    menuHeader: {
        fontSize: ".75rem",
        padding: ".5rem 1rem",
    },
    menuItemEnvIcon: {
        height: "1.5rem",
        padding: ".5rem 1rem 1rem 0",
        color: theme.palette.tailwind.green[300],
    },
    buttonEnvIcon: {
        width: "2rem",
        height: "1.3rem",
        color: theme.palette.tailwind.green[300],
    },
    buttonEnvTitle: {
        paddingLeft: "0.5rem",
    },
}))

type EnvironmentDropdownProps = Readonly<{
    urlPrefix: string | undefined
    isAdminEnv: boolean
    compact: boolean
}>

export function EnvironmentDropdown({ urlPrefix, isAdminEnv, compact }: EnvironmentDropdownProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()
    const routeMatches = useMatches()

    const currentUserType = useAppSelector(selectEffectiveUserType)

    const [dropDownAnchorEl, setDropDownAnchorEl] = useState<null | HTMLElement>(null)
    const [dropDownValue, setDropDownValue] = useState<EnvDropdownValue>(
        isAdminEnv ? EnvDropdownValue.ADMIN : EnvDropdownValue.REPORTS,
    )

    const open = Boolean(dropDownAnchorEl)

    function handleDropDownClick(event: React.MouseEvent<HTMLElement>) {
        setDropDownAnchorEl(event.currentTarget)
    }

    function handleDropDownClose() {
        setDropDownAnchorEl(null)
    }

    const isAccounting =
        routeMatches.find(
            (match) =>
                isObjectWithProp(match.handle, "environmentRoot") &&
                Boolean(match.handle.environmentRoot) &&
                isObjectWithProp(match.handle, "isAccounting") &&
                Boolean(match.handle.isAccounting),
        ) !== undefined

    useEffect(() => {
        // Update drop down value when environment changes by navigation
        if (isAdminEnv) {
            setDropDownValue(EnvDropdownValue.ADMIN)
        } else if (isAccounting) {
            setDropDownValue(EnvDropdownValue.ACCOUNTING)
        } else {
            setDropDownValue(EnvDropdownValue.REPORTS)
        }
    }, [isAdminEnv, isAccounting])

    const envIconMap: Record<EnvDropdownValue, IconDefinition> = {
        [EnvDropdownValue.ADMIN]: faSliders,
        [EnvDropdownValue.REPORTS]: faChartSimple,
        [EnvDropdownValue.ACCOUNTING]: faFileInvoiceDollar,
    }

    return (
        <>
            <Button
                data-tour="merchant-product-tour-environment"
                size="small"
                variant="text"
                onClick={handleDropDownClick}
                className={classes.envSelectButton}
            >
                <Box display="flex" alignItems="center">
                    <FontAwesomeIcon
                        className={classes.buttonEnvIcon}
                        icon={envIconMap[dropDownValue] || faQuestionCircle}
                    />
                    {!compact && (
                        <Typography className={classes.buttonEnvTitle} variant="body2">
                            {l10n.getStringForEnum(
                                "env-dropdown-title",
                                `${dropDownValue}-${currentUserType}`,
                                dropDownValue,
                            )}
                        </Typography>
                    )}
                </Box>
                {!compact && <FontAwesomeIcon icon={faAnglesUpDown} />}
            </Button>

            <Menu anchorEl={dropDownAnchorEl} open={open} onClose={handleDropDownClose} className={classes.menu}>
                <Box data-tour="merchant-product-tour-environment-content">
                    <LocalizedStrict id="env-dropdown-menu-header">
                        <Typography className={classes.menuHeader}>Switch environment to:</Typography>
                    </LocalizedStrict>
                    <Divider />
                    <MenuItem
                        data-tour="merchant-product-tour-environment-reports"
                        className={classes.menuItem}
                        onClick={() => {
                            setDropDownValue(EnvDropdownValue.REPORTS)
                            handleDropDownClose()
                            navigate(getReportRoute(urlPrefix))
                        }}
                    >
                        <ListItemIcon>
                            <FontAwesomeIcon className={classes.menuItemEnvIcon} icon={faChartSimple} />
                        </ListItemIcon>
                        <Box>
                            <Typography variant="subtitle1">
                                {l10n.getStringForEnum(
                                    "env-dropdown-title",
                                    `${EnvDropdownValue.REPORTS}-${currentUserType}`,
                                    "",
                                )}
                            </Typography>
                            <Typography className={classes.menuItemSubtitle}>
                                {l10n.getStringForEnum(
                                    "env-dropdown-subtitle",
                                    `${EnvDropdownValue.REPORTS}-${currentUserType}`,
                                    "",
                                )}
                            </Typography>
                        </Box>
                    </MenuItem>
                    <MatchContextUserType userTypes={[UserType.MERCHANT]}>
                        <Divider />
                        <MenuItem
                            className={classes.menuItem}
                            onClick={() => {
                                setDropDownValue(EnvDropdownValue.ACCOUNTING)
                                handleDropDownClose()
                                navigate(getAccountingRoute(urlPrefix))
                            }}
                        >
                            <ListItemIcon>
                                <FontAwesomeIcon className={classes.menuItemEnvIcon} icon={faFileInvoiceDollar} />
                            </ListItemIcon>
                            <Box>
                                <Typography variant="subtitle1">
                                    {l10n.getStringForEnum(
                                        "env-dropdown-title",
                                        `${EnvDropdownValue.ACCOUNTING}-${currentUserType}`,
                                        "",
                                    )}
                                </Typography>
                                <Typography className={classes.menuItemSubtitle}>
                                    {l10n.getStringForEnum(
                                        "env-dropdown-subtitle",
                                        `${EnvDropdownValue.ACCOUNTING}-${currentUserType}`,
                                        "",
                                    )}
                                </Typography>
                            </Box>
                        </MenuItem>
                    </MatchContextUserType>
                    <Divider />
                    <MenuItem
                        className={classes.menuItem}
                        onClick={() => {
                            setDropDownValue(EnvDropdownValue.ADMIN)
                            handleDropDownClose()
                            navigate(getAdminRoute(urlPrefix))
                        }}
                    >
                        <ListItemIcon>
                            <FontAwesomeIcon className={classes.menuItemEnvIcon} icon={faSliders} />
                        </ListItemIcon>
                        <Box>
                            <Typography variant="subtitle1">
                                {l10n.getStringForEnum(
                                    "env-dropdown-title",
                                    `${EnvDropdownValue.ADMIN}-${currentUserType}`,
                                    "",
                                )}
                            </Typography>

                            <Typography className={classes.menuItemSubtitle}>
                                {l10n.getStringForEnum(
                                    "env-dropdown-subtitle",
                                    `${EnvDropdownValue.ADMIN}-${currentUserType}`,
                                    "",
                                )}
                            </Typography>
                        </Box>
                    </MenuItem>
                </Box>
            </Menu>
        </>
    )
}
